package com.qrkontaq.ui.landing

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.qrkontaq.core.analytics.Analytics
import com.qrkontaq.ui.admin.AdminIcons
import com.qrkontaq.ui.tenant.MenuIcons
import com.qrkontaq.utils.SeoHelper
import kotlinx.browser.document
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.B
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.I
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Small
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul

/**
 * Brand mark: a camera viewfinder framing a QR module — "point and scan".
 * The corner brackets follow the text color, the center module pulses green.
 */
private const val LOGO_SVG =
    """<svg viewBox="0 0 32 32" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round">""" +
        """<path d="M4 10V7.5A3.5 3.5 0 0 1 7.5 4H10"/>""" +
        """<path d="M22 4h2.5A3.5 3.5 0 0 1 28 7.5V10"/>""" +
        """<path d="M28 22v2.5a3.5 3.5 0 0 1-3.5 3.5H22"/>""" +
        """<path d="M10 28H7.5A3.5 3.5 0 0 1 4 24.5V22"/>""" +
        """<g stroke="#34D399" stroke-width="1.7">""" +
        """<rect x="9.9" y="9.9" width="4.2" height="4.2" rx="1.3"/>""" +
        """<rect x="17.9" y="9.9" width="4.2" height="4.2" rx="1.3"/>""" +
        """<rect x="9.9" y="17.9" width="4.2" height="4.2" rx="1.3"/>""" +
        """<rect x="17.9" y="17.9" width="4.2" height="4.2" rx="1.3"/>""" +
        """</g>""" +
        """<g fill="#34D399" stroke="none">""" +
        """<rect x="11.35" y="11.35" width="1.3" height="1.3" rx="0.45"/>""" +
        """<rect x="19.35" y="11.35" width="1.3" height="1.3" rx="0.45"/>""" +
        """<rect x="11.35" y="19.35" width="1.3" height="1.3" rx="0.45"/>""" +
        """<rect x="19.35" y="19.35" width="1.3" height="1.3" rx="0.45"/>""" +
        """<circle class="dot" cx="16" cy="16" r="1.6"/>""" +
        """</g>""" +
        """</svg>"""

/* Hand-tuned line icons (24×24, round stroke) so the landing avoids stock emoji. */
private const val SVG_OPEN =
    """<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">"""

private val ICON_GLOBE =
    SVG_OPEN +
        """<circle cx="12" cy="12" r="8.5"/><path d="M3.5 12h17"/>""" +
        """<path d="M12 3.5c2.6 2.4 3.9 5.2 3.9 8.5s-1.3 6.1-3.9 8.5c-2.6-2.4-3.9-5.2-3.9-8.5s1.3-6.1 3.9-8.5z"/></svg>"""

private val ICON_PALETTE =
    SVG_OPEN +
        """<path d="M12 3.5a8.5 8.5 0 1 0 0 17c1.3 0 1.9-.9 1.6-2-.3-1.1.3-2 1.6-2h1.7c2 0 3.6-1.5 3.6-3.4C20.5 7.3 16.7 3.5 12 3.5z"/>""" +
        """<circle cx="8" cy="10" r="1.2" fill="currentColor" stroke="none"/>""" +
        """<circle cx="12" cy="7.6" r="1.2" fill="currentColor" stroke="none"/>""" +
        """<circle cx="16" cy="10" r="1.2" fill="currentColor" stroke="none"/></svg>"""

private val ICON_TEAM =
    SVG_OPEN +
        """<circle cx="9" cy="9" r="3.2"/>""" +
        """<path d="M3.5 19c.6-3 2.7-4.7 5.5-4.7s4.9 1.7 5.5 4.7"/>""" +
        """<path d="M15.5 6.3a3.2 3.2 0 0 1 0 6.2"/><path d="M17.2 14.6c1.9.7 3 2.1 3.3 4.4"/></svg>"""

private val ICON_APP_PIN =
    SVG_OPEN +
        """<rect x="6" y="3.5" width="12" height="17" rx="3"/>""" +
        """<path d="M12 8c1.8 0 3.2 1.4 3.2 3.1 0 2.3-3.2 5.1-3.2 5.1s-3.2-2.8-3.2-5.1C8.8 9.4 10.2 8 12 8z"/>""" +
        """<circle cx="12" cy="11" r="1" fill="currentColor" stroke="none"/></svg>"""

private val ICON_TAG =
    SVG_OPEN +
        """<path d="M4 5.5C4 4.7 4.7 4 5.5 4h5.2c.4 0 .8.2 1.1.4l8.2 8.2c.6.6.6 1.6 0 2.2l-5.2 5.2c-.6.6-1.6.6-2.2 0L4.4 11.8a1.5 1.5 0 0 1-.4-1.1V5.5z"/>""" +
        """<circle cx="8.3" cy="8.3" r="1.3" fill="currentColor" stroke="none"/></svg>"""

private val ICON_BRUSH =
    SVG_OPEN +
        """<path d="M16.8 3.9l3.3 3.3-8.8 8.8-4.2.9.9-4.2 8.8-8.8z"/>""" +
        """<path d="M6.6 16.6c-2 .3-3 1.4-3.4 3.9 2.9.4 5.4-.5 5.8-2.4"/></svg>"""

private val ICON_QR =
    SVG_OPEN +
        """<rect x="4" y="4" width="6.6" height="6.6" rx="1.6"/>""" +
        """<rect x="13.4" y="4" width="6.6" height="6.6" rx="1.6"/>""" +
        """<rect x="4" y="13.4" width="6.6" height="6.6" rx="1.6"/>""" +
        """<path d="M13.8 13.8h2.6v2.6h-2.6zM17.4 17.4H20V20h-2.6z" fill="currentColor" stroke="none"/></svg>"""

private val ICON_TREND =
    SVG_OPEN +
        """<path d="M4 17l5-5 3.5 3.5L19 9"/><path d="M15.5 9H19v3.5"/></svg>"""

private val ICON_FUNNEL =
    SVG_OPEN +
        """<path d="M4 5h16l-6.2 7.3V18l-3.6 2.2v-7.9L4 5z"/></svg>"""

private val ICON_STAR =
    """<svg viewBox="0 0 24 24" fill="currentColor" stroke="none">""" +
        """<path d="M12 3.2l2.5 5.4 5.9.7-4.4 4 1.2 5.8-5.2-3-5.2 3 1.2-5.8-4.4-4 5.9-.7z"/></svg>"""

private val ICON_BELL =
    SVG_OPEN +
        """<path d="M10.268 21a2 2 0 0 0 3.464 0"/>""" +
        """<path d="M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326"/></svg>"""

private val ICON_RECEIPT =
    SVG_OPEN +
        """<path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z"/>""" +
        """<path d="M14 8H8"/><path d="M16 12H8"/><path d="M13 16H8"/></svg>"""

/** Chip thumbnails for the phone-mock menu rail (by chip index; the "All" tab has none). */
private val CHIP_IMGS = listOf<String?>(null, "/landing/pizza.jpg", "/landing/pasta.jpg", "/landing/dessert.jpg")

/** 11×11 pseudo-QR used in the intro, the hero float card and the print mock. */
private val QR_PATTERN =
    (
        "11101010111" +
            "10001101101" +
            "10101010101" +
            "10001011001" +
            "11111010111" +
            "00010100010" +
            "10111011101" +
            "01000110010" +
            "11101011111" +
            "10010101001" +
            "11101101011"
    ).map { it == '1' }

/**
 * 17×17 pseudo-QR for the final CTA: proper finder squares in three corners so it
 * reads as a QR at a glance, deterministic noise elsewhere. Cells repel the cursor.
 */
private val BIG_QR: List<Boolean> = run {
    val n = 17
    List(n * n) { i ->
        val x = i % n
        val y = i / n
        val inFinder = (x < 7 && y < 7) || (x >= n - 7 && y < 7) || (x < 7 && y >= n - 7)
        if (inFinder) {
            val fx = if (x >= n - 7) x - (n - 7) else x
            val fy = if (y >= n - 7) y - (n - 7) else y
            fx == 0 || fx == 6 || fy == 0 || fy == 6 || (fx in 2..4 && fy in 2..4)
        } else {
            (x * 31 + y * 17 + x * y * 7) % 9 < 4
        }
    }
}

/**
 * The scan thread: one SVG line drawn through the whole page as you scroll,
 * weaving between chapters behind the cards, with a glowing tip. The path is
 * built at runtime from the chapter anchors (see buildThread in LANDING_JS).
 */
private const val THREAD_SVG =
    """<svg id="qx-thread" xmlns="http://www.w3.org/2000/svg">""" +
        """<defs><linearGradient id="qxTG" x1="0" y1="0" x2="0" y2="1">""" +
        """<stop offset="0" stop-color="#34D399" stop-opacity="0"/>""" +
        """<stop offset="0.05" stop-color="#34D399" stop-opacity="0.55"/>""" +
        """<stop offset="0.95" stop-color="#7EE2BC" stop-opacity="0.55"/>""" +
        """<stop offset="1" stop-color="#7EE2BC" stop-opacity="0"/></linearGradient></defs>""" +
        """<path id="qx-thread-path" d="" fill="none" stroke="url(#qxTG)" stroke-width="2" stroke-linecap="round"/>""" +
        """<g id="qx-thread-tip" transform="translate(-100 -100)">""" +
        """<circle r="11" fill="rgba(52,211,153,0.18)"/>""" +
        """<circle r="4" fill="#7EE2BC"/></g></svg>"""

@Composable
fun LandingPage() {
    InjectLandingStyles()
    val language = landingLanguage
    LaunchedEffect(language) {
        document.documentElement?.setAttribute("lang", language.code)
        // Localized title/description + a self-canonical per ?lang= entry point:
        // Google renders JS, so each language URL indexes with its own copy.
        val strings = language.strings
        SeoHelper.updatePageMeta(title = strings.seoTitle, description = strings.seoDescription)
        // FAQPage schema in the rendered language, landing only (see SeoHelper).
        SeoHelper.setFaqJsonLd(strings.faqs.map { it.question to it.answer })
        SeoHelper.setCanonical(
            if (landingUrlLanguage() != null) {
                "https://qrkontaq.com/?lang=${language.code}"
            } else {
                "https://qrkontaq.com/"
            },
        )
        // Effects run after the key(language) subtree is attached, so the
        // observer always binds to the freshly rebuilt section nodes.
        Analytics.observeSections(listOf("how", "features", "reviews", "pricing", "faq"))
    }
    Div({ classes("qx-root") }) {
        // Outside key(language): the intro plays once per session and must not
        // restart when the visitor switches language.
        IntroOverlay()
        // One shared background scene for the whole page: light blobs that drift
        // and re-hue with scroll, a soft grid, ambient QR "dust". Sections are
        // transparent, so the entire landing reads as a single moving canvas.
        Div({ classes("qx-scene") }) {
            Div({ classes("qx-scene-blobs"); id("qx-scene-blobs") }) { I(); I(); I() }
            Div({ classes("qx-scene-grid"); id("qx-scene-grid") })
            SCENE_BITS.forEach { bit ->
                Div({
                    classes("qx-bit", if (bit.filled) "f" else "o")
                    style {
                        property("left", bit.left)
                        property("top", bit.top)
                        property("width", "${bit.size}px")
                        property("height", "${bit.size}px")
                        property("--t", bit.period)
                        property("--dd", bit.delay)
                    }
                })
            }
        }
        // The scan thread lives behind the content (sections are z-indexed above).
        Div({
            classes("qx-thread-wrap")
            ref { el ->
                el.innerHTML = THREAD_SVG
                onDispose { }
            }
        })
        Div({ classes("qx-noise") })
        // Cursor-following light pool over the dark canvas.
        Div({ classes("qx-lamp") })
        // 49-cell QR that morphs into the active chapter number (JS-driven).
        Div({ classes("qx-morph"); id("qx-morph") }) { repeat(49) { I() } }
        Div({ classes("ql-scanline"); id("qx-scanline") })
        Div({ classes("ql-cursor-ring"); id("ql-cursor-ring") })
        Div({ classes("ql-cursor-dot"); id("ql-cursor-dot") })
        Header()
        // Keyed by language: fine-grained recomposition leaves some leaf
        // texts stale on live switches, so rebuild the whole subtree instead.
        // The landing JS re-attaches via its MutationObservers.
        key(language) {
            Hero()
            Marquee()
            HowDeck()
            FeaturesRail()
            Stats()
            BandWord()
            Reviews()
            PricingSection()
            FaqSection()
            FinalCta()
            Footer()
        }
    }
}

/* ---------------------------------- intro ----------------------------------- */

/** Boot scan: the QR assembles cell by cell, a laser sweeps it, the page opens. */
@Composable
private fun IntroOverlay() {
    Div({ classes("qx-intro"); id("qx-intro") }) {
        Div({ classes("qx-intro-box") }) {
            Div({ classes("qx-intro-qr") }) {
                QR_PATTERN.forEachIndexed { index, on ->
                    I({
                        if (on) {
                            style { property("--d", "${(index % 13) * 55 + (index / 13) * 25}ms") }
                        } else {
                            style { property("opacity", "0"); property("animation", "none") }
                        }
                    })
                }
            }
            Div({ classes("qx-intro-laser") })
        }
        Div({ classes("qx-intro-brand", "qx-display") }) { Text("QRkontaq") }
    }
}

/* ---------------------------------- header ---------------------------------- */

@Composable
internal fun Logo() {
    Div({ classes("ql-logo", "ql-display") }) {
        Span({ classes("ql-logo-mark") }) { Icon(LOGO_SVG) }
        Span({ classes("ql-logo-word") }) { Text("QRkontaq") }
    }
}

@Composable
private fun Header() {
    val t = LT
    Div({ classes("qx-header"); id("qx-header") }) {
        Div({ classes("qx-wrap") }) {
            Div({ classes("qx-header-in") }) {
                A(href = "/") { Logo() }
                Div({ classes("ql-nav") }) {
                    Div({ classes("ql-nav", "ql-nav-links") }) {
                        A(href = "#how", { classes("ql-nav-link"); attr("data-qk", "nav-how") }) { RollText(t.navHow) }
                        A(href = "#features", { classes("ql-nav-link"); attr("data-qk", "nav-features") }) { RollText(t.navFeatures) }
                        A(href = "#pricing", { classes("ql-nav-link"); attr("data-qk", "nav-pricing") }) { RollText(t.navPricing) }
                        A(href = "#faq", { classes("ql-nav-link"); attr("data-qk", "nav-faq") }) { RollText(t.navFaq) }
                    }
                    LanguageMenu()
                    A(href = "/admin", { classes("ql-btn", "small", "ql-header-cta"); attr("data-qk", "nav-cta") }) {
                        Span({ classes("full") }) { Text(t.navCta) }
                        Span({ classes("short") }) { Text(t.navCtaShort) }
                    }
                    MobileMenu()
                }
            }
        }
    }
}

/**
 * Burger menu shown on narrow screens instead of the inline nav links.
 * Holds its own state so toggling never recomposes the header div — the JS
 * "scrolled" class lives on its class attribute and would be wiped.
 */
@Composable
private fun MobileMenu() {
    var open by remember { mutableStateOf(false) }
    val t = LT
    Div({ classes("ql-mmenu-wrap") }) {
        Div({
            classes("ql-burgerbtn")
            if (open) classes("open")
            onClick { open = !open }
        }) { I(); I(); I() }
        if (open) {
            Div({ classes("ql-mmenu-backdrop"); onClick { open = false } })
            Div({ classes("ql-mmenu") }) {
                listOf(
                    "#how" to t.navHow,
                    "#features" to t.navFeatures,
                    "#pricing" to t.navPricing,
                    "#faq" to t.navFaq,
                ).forEach { (href, label) ->
                    A(href = href, {
                        classes("ql-mmenu-link")
                        attr("data-qk", "nav-" + href.removePrefix("#"))
                        onClick { open = false }
                    }) { Text(label) }
                }
                Div({ classes("ql-mmenu-langs") }) {
                    LandingLanguage.entries.forEach { language ->
                        Div({
                            classes("ql-mmenu-lang")
                            if (language == landingLanguage) classes("on")
                            onClick { setLandingLanguage(language) }
                        }) { Text(language.shortLabel) }
                    }
                }
                A(href = "/admin", { classes("ql-btn", "small", "ql-mmenu-cta"); attr("data-qk", "nav-cta") }) { Text(t.navCta) }
            }
        }
    }
}

/** Nav label that rolls to its mint twin on hover (two stacked copies). */
@Composable
private fun RollText(label: String) {
    Span({ classes("qx-roll") }) {
        Span({ classes("t1") }) { Text(label) }
        Span({ classes("t2") }) { Text(label) }
    }
}

@Composable
private fun LanguageMenu() {
    var open by remember { mutableStateOf(false) }
    val current = landingLanguage
    Div({ classes("ql-lang"); if (open) classes("open") }) {
        Div({ classes("ql-lang-btn"); attr("data-qk", "lang-menu"); onClick { open = !open } }) {
            Span({ classes("ql-lang-globe") }) { Icon(ICON_GLOBE) }
            Span { Text(current.shortLabel) }
            Span({ classes("ql-lang-caret") }) { Text("▾") }
        }
        if (open) {
            Div({ classes("ql-lang-backdrop"); onClick { open = false } })
            Div({ classes("ql-lang-menu") }) {
                LandingLanguage.entries.forEach { language ->
                    Div({
                        classes("ql-lang-item")
                        if (language == current) classes("on")
                        onClick {
                            setLandingLanguage(language)
                            open = false
                        }
                    }) {
                        Span { Text(language.nativeName) }
                        if (language == current) Span({ classes("check") }) { Text("✓") }
                    }
                }
            }
        }
    }
}

/* ----------------------------------- hero ----------------------------------- */

/** Decorative QR "bits" drifting on the shared scene layer (viewport coords). */
private data class HeroBit(
    val left: String,
    val top: String,
    val size: Int,
    val filled: Boolean,
    val period: String,
    val delay: String,
)

private val SCENE_BITS = listOf(
    HeroBit("5%", "20%", 10, true, "9s", "0s"),
    HeroBit("11%", "66%", 14, false, "11s", "-2s"),
    HeroBit("28%", "12%", 8, true, "10s", "-4s"),
    HeroBit("55%", "8%", 12, false, "12s", "-1s"),
    HeroBit("84%", "14%", 9, true, "10s", "-3s"),
    HeroBit("93%", "52%", 13, false, "9s", "-5s"),
    HeroBit("72%", "84%", 10, true, "11s", "-2.5s"),
    HeroBit("38%", "90%", 8, false, "8.5s", "-1.5s"),
    HeroBit("7%", "42%", 9, false, "10.5s", "-3.5s"),
    HeroBit("48%", "55%", 7, true, "12s", "-6s"),
    HeroBit("90%", "32%", 8, false, "9.5s", "-1s"),
    HeroBit("22%", "88%", 11, true, "10s", "-4.5s"),
)

@Composable
private fun Hero() {
    Div({ classes("qx-hero"); id("qx-hero") }) {
        Div({ classes("qx-heroword", "qx-display"); attr("data-par", "70") }) { Text("QR·MENU") }
        Div({ classes("qx-wrap") }) {
            Div({ classes("qx-hero-in") }) {
                HeroCopy()
                HeroVisual()
            }
        }
        Div({ classes("qx-shint") })
    }
}

@Composable
private fun HeroCopy() {
    val t = LT
    Div({ classes("qx-hero-copy") }) {
        Div({ classes("qx-badge", "qx-fade") }) {
            I()
            Text(t.heroBadge)
        }
        // Keyed by language so the word-mask animation replays with the new words.
        key(landingLanguage) {
            H1({ classes("qx-h1", "qx-display") }) {
                t.heroTitle.forEachIndexed { index, word ->
                    if (index > 0) Text(" ")
                    MaskWord(word.text, 150 + index * 95, word.gradient)
                }
            }
        }
        P({ classes("qx-hero-sub", "qx-fade"); style { property("--d", "0.55s") } }) {
            Text(t.heroSub)
        }
        Div({ classes("qx-hero-ctas", "qx-fade"); style { property("--d", "0.68s") } }) {
            A(href = "/admin", { classes("ql-btn"); attr("data-qk", "cta-hero-start") }) { Text(t.heroCtaTrial) }
            A(href = "#how", { classes("ql-btn-ghost"); attr("data-qk", "cta-hero-how") }) { Text(t.heroCtaHow) }
        }
        Div({ classes("qx-hero-note", "qx-fade"); style { property("--d", "0.8s") } }) {
            t.heroNotes.forEach { note ->
                Span {
                    if (note.pre.isNotEmpty()) Text(note.pre)
                    B(note.bold)
                    if (note.post.isNotEmpty()) Text(note.post)
                }
            }
        }
    }
}

@Composable
private fun B(text: String) {
    org.jetbrains.compose.web.dom.B { Text(text) }
}

@Composable
private fun MaskWord(word: String, delayMs: Int, gradient: Boolean = false) {
    Span({ classes("qx-mask") }) {
        Span({
            classes("w")
            if (gradient) classes("ql-grad-text")
            style { property("--d", "${delayMs}ms") }
        }) { Text(word) }
    }
}

@Composable
private fun Icon(svg: String) {
    Span({
        classes("ql-svg")
        ref { el ->
            el.innerHTML = svg
            onDispose { }
        }
    })
}

@Composable
private fun Sticker(text: String, color: String, top: String? = null, right: String? = null, left: String? = null, bottom: String? = null, rotate: String = "-6deg") {
    Div({
        classes("ql-sticker", color)
        style {
            top?.let { property("top", it) }
            right?.let { property("right", it) }
            left?.let { property("left", it) }
            bottom?.let { property("bottom", it) }
            property("--r", rotate)
        }
    }) { Text(text) }
}

@Composable
private fun QrGrid(cellDelayMs: Int = 0) {
    QR_PATTERN.forEachIndexed { i, on ->
        if (on) {
            I({
                if (cellDelayMs > 0) {
                    style { property("--d", "${(i % 17) * cellDelayMs}ms") }
                }
            })
        } else {
            I({ style { property("opacity", "0"); property("animation", "none") } })
        }
    }
}

@Composable
private fun HeroVisual() {
    val t = LT
    Div({ classes("ql-hero-visual") }) {
        Div({ classes("ql-ring", "r1") }) { Div({ classes("dot") }) }
        Div({ classes("ql-ring", "r2") }) { Div({ classes("dot") }) }

        Div({ classes("ql-phone-wrap"); id("ql-phone-wrap") }) {
            Sticker(t.phoneSticker, "coral", top = "-14px", right = "-30px", rotate = "7deg")
            Div({ classes("ql-phone") }) {
                Div({ classes("ql-notch") })
                // Miniature replica of the real guest menu site (MenuScreen).
                Div({ classes("ql-screen") }) { MockScreen() }
            }

            HeroFloatCards(t)
        }
    }
}

/** The guest-menu replica screen, shared by the hero phone and the fly phone. */
@Composable
private fun MockScreen() {
    val t = LT
    Div({ classes("ql-mcover") }) {
                        Div({ classes("ql-mcover-scrim") })
                        Div({ classes("ql-mlogo") }) { Text("B") }
                        Div({ classes("ql-mtop") }) {
                            Div({ classes("ql-mlang") }) {
                                Span({ classes("on") }) { Text("EN") }
                                Span { Text("ES") }
                            }
                            Div({ classes("ql-mburger") }) { I(); I(); I() }
                        }
                        Div({ classes("ql-mcover-bottom") }) {
                            Div({ classes("ql-mcover-name") }) { Text("Bella Vista") }
                            Div({ classes("ql-mcover-meta") }) {
                                Span({ classes("pill") }) {
                                    Span({ classes("dot") })
                                    Text(t.pillOpen)
                                }
                                Span({ classes("pill") }) {
                                    Span({ classes("star") }) { Text("★") }
                                    Text("4.9 (124)")
                                }
                                Span({ classes("pill") }) { Text(t.pillLangs) }
                            }
                        }
                        // Filters button pinned to the banner's bottom-right corner,
                        // matching the real guest menu.
                        Span({ classes("ql-mfilter") }) { Icon(ICON_FUNNEL) }
                    }
                    Div({ classes("ql-mrail") }) {
                        Div({ classes("ql-mrail-chips") }) {
                            t.chips.forEachIndexed { index, chip ->
                                val chipImg = CHIP_IMGS.getOrNull(index)
                                Span({
                                    classes("ql-mchip")
                                    if (index == 0) classes("on")
                                    if (chipImg != null) classes("has-img")
                                }) {
                                    chipImg?.let { Img(src = it, alt = chip) }
                                    Span { Text(chip) }
                                }
                            }
                        }
                    }
                    Div({ classes("ql-msec") }) {
                        Span({ classes("num") }) { Text("01") }
                        Span({ classes("t") }) { Text(t.mockSection) }
                    }
                    val likes = listOf("48", "32", "27", "21")
                    // First dish is the featured photo card (as on the business card),
                    // the rest are compact rows.
                    t.dishes.firstOrNull()?.let { dish -> MockFeature(dish, likes[0]) }
                    t.dishes.drop(1).take(2).forEachIndexed { index, dish ->
                        MockDish(
                            dish = dish,
                            likes = likes.getOrElse(index + 1) { "12" },
                            delayMs = 450 + index * 180,
                        )
                    }
                    Div({ classes("ql-mfoot") }) {
                        Span({ classes("ql-mfoot-mark") }) { Icon(LOGO_SVG) }
                        Text("Powered by QRkontaq")
                    }
                    // Floating waiter-call + my-check pills, as on the real menu.
                    Div({ classes("ql-mcheck") }) {
                        Icon(ICON_RECEIPT)
                        Text(t.mockCheck)
                    }
                    Div({ classes("ql-mwaiter") }) {
                        Icon(ICON_BELL)
                        Text(t.mockWaiter)
                    }
}

@Composable
private fun HeroFloatCards(t: LandingStrings) {
            Div({ classes("ql-float-card", "ql-fc-qr") }) {
                Div({ classes("ql-qr") }) { QrGrid(cellDelayMs = 90) }
                Span { B(t.fcScan) }
            }
            Div({ classes("ql-float-card", "ql-fc-stop") }) {
                Div({ classes("ql-toggle") })
                Span {
                    B(t.fcStop)
                    Small { Text(t.fcStopSub) }
                }
            }
            Div({ classes("ql-float-card", "ql-fc-scans") }) {
                Span({ classes("ql-fc-ic") }) { Icon(ICON_TREND) }
                Span {
                    B(t.fcScans)
                    Small { Text(t.fcScansSub) }
                }
            }
}

@Composable
private fun MockFeature(dish: MockDishData, likes: String) {
    // Large photo hero for the first dish, mirroring the guest menu's featured card:
    // full-bleed image, likes badge, name + meta + price over a bottom gradient.
    Div({
        classes("ql-mfeat")
        style { property("background-image", "url('/landing/${dish.img}.jpg')") }
    }) {
        Div({ classes("ql-mfeat-grad") })
        Div({ classes("ql-mfeat-likes") }) {
            Span({ classes("ql-fheart") }) { Text("♥") }
            Text(likes)
        }
        Div({ classes("ql-mfeat-bot") }) {
            Div {
                Div({ classes("ql-mfeat-name") }) { Text(dish.name) }
                Div({ classes("ql-mfeat-meta") }) { Text(dish.sub) }
                if (dish.meta.isNotEmpty()) {
                    Div({ classes("ql-mfeat-nutri") }) { Text(dish.meta) }
                }
            }
            Span({ classes("ql-mfeat-price") }) { Text(dish.price) }
        }
    }
}

@Composable
private fun MockDish(dish: MockDishData, likes: String, delayMs: Int) {
    // Mirrors the real dish card: text on the left, price in the venue's
    // accent color, likes counter, photo on the right.
    Div({ classes("ql-mdish"); style { property("animation-delay", "${delayMs}ms") } }) {
        Div({ classes("ql-mdish-body") }) {
            Div({ classes("ql-mdish-name") }) { Text(dish.name) }
            Div({ classes("ql-mdish-sub") }) { Text(dish.sub) }
            if (dish.meta.isNotEmpty()) {
                Div({ classes("ql-mdish-nutri") }) { Text(dish.meta) }
            }
            Div({ classes("ql-mdish-meta") }) {
                Span({ classes("ql-mdish-price") }) { Text(dish.price) }
                Span({ classes("ql-mlikes") }) {
                    Span({ classes("ql-mheart") }) { Text("♥") }
                    Text(likes)
                }
            }
        }
        Div({ classes("ql-mimg", dish.img) })
    }
}

/* ---------------------------------- marquee ---------------------------------- */

@Composable
private fun Marquee() {
    val t = LT
    Div({ classes("ql-marquee") }) {
        Div({ classes("ql-mq-track") }) {
            repeat(2) {
                t.marquee.forEach { item ->
                    Span({ classes("ql-mq-item") }) {
                        Text(item)
                        I()
                    }
                }
            }
        }
    }
}

/* ------------------------------- chapter heads ------------------------------- */

/**
 * Editorial chapter head: huge outlined index, overline + dotted leader +
 * Playfair-italic note (a nod to printed menus), then the big title.
 */
@Composable
private fun Chapter(num: String, overline: String, title: String, note: String, sub: String? = null) {
    Div({ classes("qx-chap", "qx-reveal") }) {
        Div({ classes("qx-chap-num", "qx-display"); attr("data-par", "26") }) { Text(num) }
        Div({ classes("qx-chap-bd") }) {
            Div({ classes("qx-chap-top") }) {
                Span({ classes("qx-overline") }) { Text(overline) }
                Span({ classes("qx-chap-line") })
                if (note.isNotEmpty()) Span({ classes("qx-chap-note") }) { Text(note) }
            }
            H2({ classes("qx-chap-title", "qx-display") }) { Text(title) }
            sub?.let { P({ classes("qx-chap-sub") }) { Text(it) } }
        }
    }
}

/* ------------------------- how it works: pinned deck ------------------------- */

/**
 * The three steps as a pinned card deck: the section is 320vh tall, the viewport
 * sticks, and the JS engine slides each next card over the previous one while
 * scaling and dimming what is underneath. On phones it degrades to a plain stack.
 */
@Composable
private fun HowDeck() {
    val t = LT
    Div({ classes("qx-section", "qx-how"); id("how") }) {
        Div({ classes("qx-wrap") }) {
            Chapter("01", t.stepsOverline, t.stepsTitle, t.stepsNote, t.stepsSub)
        }
        Div({ classes("qx-deck"); id("qx-deck") }) {
            Div({ classes("qx-deck-pin") }) {
                val icons = listOf(ICON_TAG, ICON_BRUSH, ICON_QR)
                t.steps.forEachIndexed { index, step ->
                    DeckCard(index, icons[index], step, t)
                }
            }
        }
    }
}

@Composable
private fun DeckCard(index: Int, icon: String, step: LandingStep, t: LandingStrings) {
    Div({ classes("qx-card", "qx-spot") }) {
        Div({ classes("qx-card-num", "qx-display") }) { Text("0${index + 1}") }
        Div({ classes("qx-card-l") }) {
            Div({ classes("qx-card-badge") }) { Icon(icon) }
            Div({ classes("qx-card-title", "qx-display") }) { Text(step.title) }
            P({ classes("qx-card-text") }) { Text(step.text) }
            Div({ classes("qx-card-step") }) {
                I()
                Text("0${index + 1} / 0${t.steps.size}")
            }
        }
        Div({ classes("qx-card-r") }) {
            when (index) {
                0 -> DeckVisMenu(t)
                1 -> DeckVisBrand()
                else -> DeckVisQr(t)
            }
        }
    }
}

/** Step 1 visual: real dish rows, as in the admin's menu tab. */
@Composable
private fun DeckVisMenu(t: LandingStrings) {
    Div({ classes("qx-vis-menu") }) {
        t.dishes.take(3).forEach { dish ->
            Div({ classes("ql-adm-row") }) {
                Div({ classes("ql-mimg", dish.img) })
                Div({ classes("bd") }) {
                    Div({ classes("nm2") }) { Text(dish.name) }
                    Div({ classes("sb") }) { Text(dish.sub) }
                }
                Span({ classes("price") }) { Text(dish.price) }
            }
        }
    }
}

/** Step 2 visual: branded cover preview + the venue's palette. */
@Composable
private fun DeckVisBrand() {
    Div({ classes("qx-vis-brand") }) {
        Div({ classes("qx-brandprev") }) {
            Div({ classes("bp-logo") }) { Text("B") }
            Div({ classes("bp-name") }) { Text("Bella Vista") }
        }
        Div({ classes("ql-swatches") }) {
            listOf("#C4573A", "#0F9D6E", "#E8A33D", "#7C5CBF", "#1F2421").forEach { hex ->
                Div({ classes("ql-swatch"); style { property("background", hex) } })
            }
        }
    }
}

/** Step 3 visual: the printable table QR. */
@Composable
private fun DeckVisQr(t: LandingStrings) {
    Div({ classes("qx-vis-qr") }) {
        Div({ classes("ql-qr") }) { QrGrid(cellDelayMs = 60) }
        Span({ classes("qx-pdfchip") }) { Text(t.adminDownloadPdf) }
    }
}

/* ---------------------- features: pinned horizontal rail ---------------------- */

/**
 * Feature panels ride a horizontal rail: the 300vh section pins the viewport and
 * vertical scroll drives the track sideways. On phones it becomes a native
 * scroll-snap carousel. The admin showcase follows inside the same #features
 * section so the analytics observer keeps its single "features" entry.
 */
@Composable
private fun FeaturesRail() {
    val t = LT
    Div({ classes("qx-section", "qx-feats"); id("features") }) {
        Div({ classes("qx-wrap") }) {
            Chapter("02", t.featuresOverline, t.featuresTitle, t.featuresNote, t.featuresSub)
        }
        Div({ classes("qx-rail"); id("qx-rail") }) {
            Div({ classes("qx-rail-pin") }) {
                Div({ classes("qx-ghost", "qx-display") }) { Text(t.ghostWord) }
                Div({ classes("qx-rail-track"); id("qx-rail-track") }) {
                    RailPanel(0, ICON_GLOBE, t.tileSubdomainTitle, t.tileSubdomainText) { SubdomainMock() }
                    RailPanel(1, ICON_PALETTE, t.tileBrandTitle, t.tileBrandText) { BrandMock(t) }
                    RailPanel(2, ICON_TEAM, t.tileTeamTitle, t.tileTeamText) { TeamMock() }
                    RailPanel(3, ICON_APP_PIN, t.tileAppTitle, t.tileAppText) { MapMock() }
                }
            }
        }
        AdminSection()
    }
}

@Composable
private fun RailPanel(index: Int, icon: String, title: String, text: String, mock: @Composable () -> Unit) {
    // Outer div: coverflow tilt target for the JS engine; inner div: reveal + spotlight.
    Div({ classes("qx-panel") }) {
        Div({ classes("qx-panel-in", "qx-spot", "qx-reveal"); style { property("--d", "${index * 90}ms") } }) {
            Div({ classes("qx-panel-top") }) {
                Div({ classes("qx-panel-icon") }) { Icon(icon) }
                Span({ classes("qx-panel-num", "qx-display") }) { Text("0${index + 1}") }
            }
            Div({ classes("qx-panel-title") }) { Text(title) }
            P({ classes("qx-panel-text") }) { Text(text) }
            Div({ classes("qx-panel-mock") }) { mock() }
        }
    }
}

@Composable
private fun SubdomainMock() {
    val t = LT
    Div({ classes("ql-browser") }) {
        Div({ classes("ql-browser-bar") }) {
            Span({ classes("ql-bdot", "r") })
            Span({ classes("ql-bdot", "y") })
            Span({ classes("ql-bdot", "g") })
            Div({ classes("ql-burl") }) {
                Span({ classes("ql-type") }) {
                    Span({ classes("nm") }) { Text("bella-vista") }
                    Text(".qrkontaq.com")
                }
            }
        }
        Div({ classes("ql-browser-body") }) {
            Div({ classes("ql-bb-logo") }) { Text("B") }
            Div({ style { property("flex", "1"); property("min-width", "0") } }) {
                Div({ classes("ql-bb-name") }) { Text("Bella Vista") }
                Div({
                    classes("ql-bb-line")
                    style {
                        property("width", "68%")
                        property("margin-top", "7px")
                        property("opacity", "0.6")
                    }
                })
            }
            Span({ classes("ql-url-hint") }) { Text("↖ " + t.urlHint) }
        }
    }
}

@Composable
private fun BrandMock(t: LandingStrings) {
    Div({ style { property("display", "flex"); property("flex-direction", "column"); property("gap", "18px") } }) {
        Div({ classes("ql-swatches") }) {
            listOf("#C4573A", "#0F9D6E", "#E8A33D", "#7C5CBF", "#1F2421").forEach { hex ->
                Div({ classes("ql-swatch"); style { property("background", hex) } })
            }
        }
        t.dishes.firstOrNull()?.let { dish ->
            Div({ classes("ql-adm-row") }) {
                Div({ classes("ql-mimg", dish.img) })
                Div({ classes("bd") }) {
                    Div({ classes("nm2") }) { Text(dish.name) }
                    Div({ classes("sb") }) { Text(dish.sub) }
                }
                Span({ classes("price") }) { Text(dish.price) }
            }
        }
    }
}

@Composable
private fun TeamMock() {
    Div({ classes("ql-avatars") }) {
        listOf(
            "В" to "linear-gradient(135deg,#0F9D6E,#0B7A55)",
            "М" to "linear-gradient(135deg,#E8A33D,#C4753A)",
            "С" to "linear-gradient(135deg,#7C5CBF,#5A3F96)",
        ).forEach { (letter, bg) ->
            Div({ classes("ql-avatar"); style { property("background", bg) } }) { Text(letter) }
        }
    }
}

@Composable
private fun MapMock() {
    Div({ classes("ql-map") }) {
        Div({ classes("ql-pin"); style { property("left", "24%"); property("top", "38%") } })
        Div({ classes("ql-pin"); style { property("left", "56%"); property("top", "24%") } })
        Div({ classes("ql-pin"); style { property("left", "70%"); property("top", "62%") } })
        Div({ classes("ql-pin"); style { property("left", "40%"); property("top", "70%") } })
    }
}

/* --------------------------- admin showcase ---------------------------- */

@Composable
private fun AdminSection() {
    val t = LT
    Div({ classes("qx-adminsec") }) {
        Div({ classes("qx-wrap") }) {
            Chapter("03", "QRkontaq Admin", t.adminTitle, "bella-vista.qrkontaq.com/admin", t.adminSub)
            Div({ classes("qx-adminshell"); attr("data-flow", "1") }) {
                // A waiter-call toast pops up periodically, like on the real tables page.
                Div({ classes("qx-belltoast") }) {
                    Icon(ICON_BELL)
                    Span { Text("${t.adminTable} 2 · ${t.adminCall}") }
                }
                AdminInteractive()
            }
        }
    }
}

/** Static mint sparkline echoing the dashboard hero card of the real admin. */
private const val SPARK_SVG =
    """<svg viewBox="0 0 120 40" xmlns="http://www.w3.org/2000/svg">""" +
        """<defs><linearGradient id="qlSpark" x1="0" y1="0" x2="0" y2="1">""" +
        """<stop offset="0%" stop-color="#7EE2BC" stop-opacity=".35"/>""" +
        """<stop offset="100%" stop-color="#7EE2BC" stop-opacity="0"/></linearGradient></defs>""" +
        """<path d="M4 30 C 14 28, 20 21, 30 23 S 48 30, 58 24 S 74 10, 84 12 S 104 9, 116 6 L 116 38 L 4 38 Z" fill="url(#qlSpark)"/>""" +
        """<path d="M4 30 C 14 28, 20 21, 30 23 S 48 30, 58 24 S 74 10, 84 12 S 104 9, 116 6" fill="none" stroke="#7EE2BC" stroke-width="2.5" stroke-linecap="round"/>""" +
        """<circle cx="116" cy="6" r="3" fill="#7EE2BC" stroke="#0B7A55" stroke-width="2"/></svg>"""

/** Sidebar entry: icon + label + tab index it activates (-1 = decorative only).
 * Mirrors the real AdminShell nav order: Overview, Menu, Stop list, Tables,
 * Orders, Team, Settings. */
private data class AdmNavItem(val icon: String, val label: String, val tab: Int)

@Composable
private fun AdminInteractive() {
    val t = LT
    var tab by remember { mutableStateOf(0) }
    var auto by remember { mutableStateOf(true) }
    LaunchedEffect(auto) {
        while (auto) {
            delay(4000)
            tab = (tab + 1) % 4
        }
    }
    Div({ classes("ql-admin-in") }) {
        Div({ classes("ql-admin-tabs") }) {
            t.adminTabs.forEachIndexed { index, data ->
                Div({
                    classes("ql-admin-tab")
                    if (index == tab) classes("on")
                    onClick {
                        tab = index
                        auto = false
                    }
                }) {
                    Div({ classes("num", "ql-display") }) { Text("0${index + 1}") }
                    Div({ classes("bd") }) {
                        Div({ classes("tt") }) { Text(data.title) }
                        Div({ classes("tx") }) { Text(data.text) }
                    }
                    if (index == tab && auto) {
                        // Keyed so the DOM node is recreated per tab and the fill animation restarts.
                        key(tab) { Div({ classes("bar") }) { I() } }
                    }
                }
            }
        }
        Div({ classes("ql-admin-stage") }) {
            AdminBrowser(tab)
        }
    }
}

@Composable
private fun AdminBrowser(tab: Int) {
    val t = LT
    Div({ classes("ql-browser", "ql-adm-frame") }) {
        Div({ classes("ql-browser-bar") }) {
            Span({ classes("ql-bdot", "r") })
            Span({ classes("ql-bdot", "y") })
            Span({ classes("ql-bdot", "g") })
            Div({ classes("ql-burl") }) {
                Span {
                    Span({ classes("nm") }) { Text("bella-vista") }
                    Text(".qrkontaq.com/admin")
                }
            }
        }
        Div({ classes("ql-adm") }) {
            // On phones the sidebar collapses into this mock mobile top bar,
            // just like the real admin shell does.
            Div({ classes("ql-adm-mtop") }) {
                Div({ classes("mk") }) { Text("B") }
                Span({ classes("nm3") }) { Text("Bella Vista") }
                Div({ classes("mb") }) { I(); I(); I() }
            }
            // Sidebar replicates the real AdminShell: venue identity, mint
            // "open site" pill, icon navigation on the dark-green gradient.
            Div({ classes("ql-adm-side") }) {
                Div({ classes("ql-adm-brand") }) {
                    Div({ classes("mk") }) { Text("B") }
                    Div({ classes("bd") }) {
                        Div({ classes("nm3") }) { Text("Bella Vista") }
                        Div({ classes("rl") }) { Text(t.adminRole) }
                    }
                }
                Div({ classes("ql-adm-open") }) {
                    Icon(AdminIcons.ExternalLink)
                    Span { Text(t.adminOpenSite) }
                }
                // Same items and order as the real AdminShell owner sidebar
                // (the stop list lives inside Menu there, not in the nav).
                listOf(
                    AdmNavItem(AdminIcons.Dashboard, t.adminTabs[3].label, 3),
                    AdmNavItem(MenuIcons.Utensils, t.adminTabs[0].label, 0),
                    AdmNavItem(AdminIcons.QrCode, t.adminTabs[2].label, 2),
                    AdmNavItem(AdminIcons.ChefHat, t.adminTabs[1].label, 1),
                    AdmNavItem(AdminIcons.Users, t.adminNavTeam, -1),
                    AdmNavItem(AdminIcons.Sliders, t.adminNavSettings, -1),
                    AdmNavItem(AdminIcons.CreditCard, t.adminNavBilling, -1),
                ).forEach { item ->
                    Div({ classes("ql-adm-nav"); if (item.tab == tab) classes("on") }) {
                        Icon(item.icon)
                        Span { Text(item.label) }
                    }
                }
            }
            key(tab) {
                Div({ classes("ql-adm-main") }) {
                    when (tab) {
                        0 -> AdmMenuPanel()
                        1 -> AdmOrdersPanel()
                        2 -> AdmTablesPanel()
                        else -> AdmOverviewPanel()
                    }
                }
            }
        }
    }
}

@Composable
private fun AdmDishRow(dish: MockDishData, content: @Composable () -> Unit) {
    Div({ classes("ql-mimg", dish.img) })
    Div({ classes("bd") }) {
        Div({ classes("nm2") }) { Text(dish.name) }
        Div({ classes("sb") }) { Text(dish.sub) }
    }
    content()
}

@Composable
private fun AdmMenuPanel() {
    val t = LT
    // Mirrors the real MenuTab: Menu/Stop-list segmented toggle, emerald price
    // pills and per-dish stop switches inside a numbered category block.
    Div({ classes("ql-adm-head") }) {
        Div({ classes("ql-adm-seg") }) {
            Span({ classes("on") }) { Text(t.adminTabs[0].label) }
            Span { Text(t.adminStopList) }
        }
        Span({ classes("ql-adm-btn") }) { Text(t.adminAddDish) }
    }
    Div({ classes("ql-adm-cat") }) {
        Span({ classes("idx") }) { Text("01") }
        Span({ classes("nm") }) { Text(t.mockSection) }
        Span({ classes("cnt") }) { Text("3") }
    }
    t.dishes.take(3).forEach { dish ->
        val off = dish.img == "dessert"
        Div({ classes("ql-adm-row"); if (off) classes("off") }) {
            AdmDishRow(dish) {
                if (off) {
                    Span({ classes("stoptag") }) { Text(t.stopTag) }
                } else {
                    Span({ classes("price") }) { Text(dish.price) }
                    Span({ classes("act") }) { Icon(AdminIcons.Pencil) }
                }
                Div({ classes("ql-adm-sw"); if (!off) classes("on") })
            }
        }
    }
}

@Composable
private fun AdmOrdersPanel() {
    val t = LT
    // Mirrors the real kitchen board: three lanes (new / cooking / ready) with
    // the real accent colors and ticket cards.
    Div({ classes("ql-adm-head") }) {
        Span({ classes("ql-adm-title") }) { Text(t.adminTabs[1].label) }
    }
    Div({ classes("ql-adm-lanes") }) {
        val lanes = listOf(
            Triple("new", listOf(3 to listOf(t.dishes[3].name)), 1),
            Triple("cook", listOf(1 to listOf(t.dishes[0].name, t.dishes[1].name)), 1),
            Triple("ready", listOf(2 to listOf(t.dishes[2].name)), 1),
        )
        lanes.forEachIndexed { index, (tone, orders, count) ->
            Div({ classes("ql-adm-lane", tone) }) {
                Div({ classes("lh") }) {
                    I()
                    Span { Text(t.adminLanes[index]) }
                    Span({ classes("cnt") }) { Text("$count") }
                }
                orders.forEach { (tableN, items) ->
                    Div({ classes("ql-adm-ocard") }) {
                        Div({ classes("tb") }) { Text("${t.adminTable} $tableN") }
                        items.forEach { name ->
                            Div({ classes("it") }) { Text("1 × $name") }
                        }
                    }
                }
            }
        }
    }
}

/** A table on the mock floor plan: label, position in %, status class. */
private data class FloorTable(val n: String, val x: Int, val y: Int, val state: String)

@Composable
private fun AdmTablesPanel() {
    val t = LT
    // Mirrors the real Tables page: the dotted floor plan with chair-and-top
    // table figures in their live status colors, plus the "+" / QR-PDF actions.
    Div({ classes("ql-adm-head") }) {
        Span({ classes("ql-adm-title") }) { Text(t.adminTabs[2].label + " (4)") }
        Div({ classes("ql-adm-tblbtns") }) {
            Span({ classes("ql-adm-plus") }) { Icon(AdminIcons.Plus) }
            Span({ classes("ql-adm-btn2") }) {
                Icon(AdminIcons.QrCode)
                Span { Text("QR PDF") }
            }
        }
    }
    Div({ classes("ql-adm-floor") }) {
        listOf(
            FloorTable("1", 20, 32, ""),
            FloorTable("2", 58, 26, "call"),
            FloorTable("3", 38, 72, "cook"),
            FloorTable("4", 81, 62, "ready"),
        ).forEach { ft ->
            Div({
                classes("ql-adm-tbl")
                if (ft.state.isNotEmpty()) classes(ft.state)
                style {
                    property("left", "${ft.x}%")
                    property("top", "${ft.y}%")
                }
            }) {
                I({ classes("ch", "n") })
                I({ classes("ch", "s") })
                I({ classes("ch", "w") })
                I({ classes("ch", "e") })
                Div({ classes("top") }) {
                    Span { Text(ft.n) }
                    when (ft.state) {
                        "call" -> Icon(MenuIcons.Bell)
                        "cook" -> Icon(AdminIcons.Flame)
                        "ready" -> Icon(AdminIcons.ChefHat)
                    }
                }
            }
        }
    }
    Div({ classes("ql-adm-legend") }) {
        Span({ classes("lg", "call") }) { I(); Text(t.adminCall) }
        Span({ classes("lg", "cook") }) { I(); Text(t.adminLanes[1]) }
        Span({ classes("lg", "ready") }) { I(); Text(t.adminLanes[2]) }
    }
}

@Composable
private fun AdmOverviewPanel() {
    val t = LT
    Div({ classes("ql-adm-head") }) {
        Span({ classes("ql-adm-title") }) { Text(t.adminTabs[3].label) }
    }
    // Mirrors the real DashboardTab: emerald-gradient hero with a mint
    // sparkline, then the soft-icon stat tiles row and the peak-hours bars.
    Div({ classes("ql-adm-hero") }) {
        Div({ classes("bd") }) {
            Div({ classes("lb") }) { Text(t.fcScansSub) }
            Div({ classes("vl") }) {
                Text(t.fcScans)
                Span({ classes("dl") }) { Text("▲ 12%") }
            }
        }
        Div({ classes("sp") }) { Icon(SPARK_SVG) }
    }
    Div({ classes("ql-adm-kpis") }) {
        Div({ classes("ql-adm-kpi") }) {
            Span({ classes("ic", "amber") }) { Text("★") }
            Div({ classes("bd") }) {
                org.jetbrains.compose.web.dom.B { Text(t.fcRating) }
                Span { Text(t.fcRatingSub) }
            }
        }
        Div({ classes("ql-adm-kpi") }) {
            Span({ classes("ic", "coral") }) { Text("♥") }
            Div({ classes("bd") }) {
                org.jetbrains.compose.web.dom.B { Text(t.dishes[0].name) }
                Span { Text(t.adminTopDish) }
            }
        }
    }
    Div({ classes("ql-adm-bars") }) {
        listOf(34, 52, 41, 66, 58, 84, 100).forEach { h ->
            I({ style { property("height", "$h%") } })
        }
    }
}

/* ----------------------------------- stats ----------------------------------- */

/**
 * The stats print out as a cash-register receipt: the paper extends from the
 * printer slot as the section scrolls in (JS drives --pp), each value rolls in
 * on odometer digit strips.
 */
@Composable
private fun Stats() {
    val t = LT
    Div({ classes("qx-stats") }) {
        Div({ classes("qx-wrap") }) {
            Div({ classes("qx-print"); attr("data-flow", "1") }) {
                Div({ classes("qx-rslot") })
                Div({ classes("qx-rwrap"); id("qx-rwrap") }) {
                    Div({ classes("qx-receipt"); id("qx-receipt") }) {
                        Div({ classes("qx-rhead") }) { Text("QRKONTAQ") }
                        Div({ classes("qx-rsub") }) { Text("qrkontaq.com") }
                        Div({ classes("qx-rdiv") })
                        t.stats.forEach { stat ->
                            Div({ classes("qx-rline") }) {
                                Span({ classes("lb") }) { Text(stat.label) }
                                Span({ classes("dots") })
                                Span({
                                    classes("vl")
                                    if (stat.count != null) {
                                        attr("data-count", stat.count)
                                        attr("data-suffix", stat.suffix)
                                    }
                                    // Static fallback = final value: crawlers and no-JS
                                    // readers must never see "0 venues" — the odometer
                                    // overwrites this once the animation kicks in.
                                }) { Text(if (stat.count != null) stat.count + stat.suffix else stat.suffix) }
                            }
                        }
                        Div({ classes("qx-rdiv") })
                        Div({ classes("qx-rbar") })
                        Span({ classes("qx-rsite") }) { Text("THANK YOU · GRACIAS · ДЯКУЄМО") }
                    }
                }
            }
        }
    }
}

/** Giant outlined word band drifting sideways with scroll — a scene divider. */
@Composable
private fun BandWord() {
    Div({ classes("qx-bandword", "qx-display") }) {
        Text("QRKONTAQ · QR MENU · QRKONTAQ · QR MENU · QRKONTAQ · QR MENU")
    }
}

/* ---------------------------------- reviews ---------------------------------- */

/**
 * Social proof as two counter-drifting marquee rows (pause on hover). Renders
 * ONLY when LANDING_REVIEWS holds real quotes — the section must never ship
 * with invented testimonials (illegal in the EU and fatal for trust).
 */
@Composable
private fun Reviews() {
    val reviews = LANDING_REVIEWS
    if (reviews.isEmpty()) return
    val t = LT
    val average = reviews.map { it.stars }.average()
    val note = "★ " + ((average * 10).toInt() / 10.0).toString()
    Div({ classes("qx-section", "qx-reviews"); id("reviews") }) {
        Div({ classes("qx-wrap") }) {
            Chapter("04", t.reviewsOverline, t.reviewsTitle, note)
        }
        Div({ classes("qx-revwrap"); attr("data-flow", "1") }) {
            Div({ classes("qx-revrow", "a") }) {
                repeat(2) { reviews.forEach { ReviewCard(it) } }
            }
            Div({ classes("qx-revrow", "b") }) {
                repeat(2) { reviews.reversed().forEach { ReviewCard(it) } }
            }
        }
    }
}

@Composable
private fun ReviewCard(review: LandingReview) {
    Div({ classes("qx-review") }) {
        Div({ classes("stars") }) {
            repeat(5) { starIndex ->
                Span({
                    classes("st")
                    if (starIndex >= review.stars) classes("off")
                }) { Icon(ICON_STAR) }
            }
        }
        P({ classes("q") }) { Text("“${review.quote}”") }
        Div({ classes("who") }) {
            Img(src = review.avatar, alt = review.name) {
                classes("av")
                attr("loading", "lazy")
                attr("width", "40")
                attr("height", "40")
            }
            Div({ classes("bd") }) {
                Div({ classes("nm") }) { Text(review.name) }
            }
        }
    }
}

/* ---------------------------------- pricing ---------------------------------- */

@Composable
private fun PricingSection() {
    val t = LT
    Div({ classes("qx-section", "qx-pricing"); id("pricing") }) {
        Div({ classes("qx-wrap") }) {
            Chapter("05", t.pricingOverline, t.pricingTitle, t.pricingNote, t.pricingSub)
            // The container reveals; each card then slams onto the "table"
            // like a paper menu, the hot plan landing last.
            Div({ classes("qx-plans", "qx-reveal") }) {
                t.plans.forEachIndexed { index, plan ->
                    Div({
                        classes("qx-plan")
                        if (plan.hot) classes("hot")
                        style { property("--d", if (plan.hot) "0.4s" else "${index * 160}ms") }
                    }) {
                        if (plan.hot) {
                            Sticker(t.hotSticker, "mint", top = "-16px", right = "18px", rotate = "5deg")
                        }
                        Div({ classes("qx-plan-name") }) { Text(plan.title) }
                        Div({ classes("qx-plan-price") }) {
                            plan.oldPrice?.let { Span({ classes("old") }) { Text(it) } }
                            B { Text(plan.price) }
                            Span { Text(plan.per) }
                        }
                        // Rendered even when empty so the cards keep identical layout.
                        Div({ classes("qx-plan-total") }) { plan.total?.let { Text(it) } }
                        plan.save?.let { Span({ classes("qx-plan-save") }) { Text(it) } }
                        Ul({ classes("qx-plan-list") }) {
                            plan.lines.forEach { line ->
                                Li {
                                    Span({ classes("qx-check") }) { Text("✓") }
                                    Span { Text(line) }
                                    Span({ classes("lead") })
                                }
                            }
                        }
                        A(href = "/admin", {
                            classes(if (plan.hot) "ql-btn" else "ql-btn-outline", "qx-plan-cta")
                            attr("data-qk", "plan-cta-" + listOf("free", "pro").getOrElse(index) { "$index" })
                        }) { Text(plan.cta) }
                    }
                }
            }
            Div({ classes("qx-pricing-note", "qx-reveal") }) { Text(t.pricingFootnote) }
        }
    }
}

/* ------------------------------------ faq ------------------------------------ */

@Composable
private fun FaqSection() {
    val t = LT
    Div({ classes("qx-section", "qx-faqsec"); id("faq") }) {
        Div({ classes("qx-wrap") }) {
            Chapter("06", t.navFaq, t.faqTitle, t.faqNote)
            Div({ classes("qx-faqs"); attr("data-flow", "1") }) {
                t.faqs.forEachIndexed { index, faq -> FaqItem(index, faq) }
            }
        }
    }
}

@Composable
private fun FaqItem(index: Int, faq: LandingFaq) {
    var open by remember { mutableStateOf(false) }
    // No qx-reveal here: recomposition rewrites the class attribute, which would wipe
    // the JS-added "in" state. The parent .qx-faqs container reveals as one block.
    Div({
        classes("qx-faq")
        if (open) classes("open")
        onClick { open = !open }
    }) {
        Div({ classes("qx-faq-q") }) {
            Span({ classes("qx-faq-idx", "qx-display") }) { Text((index + 1).toString().padStart(2, '0')) }
            Span { Text(faq.question) }
            Span({ classes("qx-faq-plus") }) { Text("+") }
        }
        Div({ classes("qx-faq-a") }) {
            Div {
                P { Text(faq.answer) }
            }
        }
    }
}

/* --------------------------------- final cta --------------------------------- */

/** Giant interactive QR: the cells scatter away from the cursor and drift back. */
@Composable
private fun FinalCta() {
    val t = LT
    Div({ classes("qx-cta") }) {
        Div({ classes("qx-bigqr"); id("qx-bigqr") }) {
            BIG_QR.forEach { on ->
                I({ if (!on) classes("off") })
            }
        }
        Div({ classes("qx-wrap") }) {
            Div({ classes("qx-cta-in"); attr("data-flow", "1") }) {
                Span({ classes("qx-cta-kick") }) { Text(t.ctaSticker) }
                H2({ classes("qx-cta-h", "qx-display") }) { Text(t.ctaTitle) }
                P({ classes("qx-cta-sub") }) { Text(t.ctaSub) }
                Div({ classes("qx-cta-btns") }) {
                    A(href = "/admin", { classes("ql-btn", "big"); attr("data-qk", "cta-final-start") }) { Text(t.ctaBtn1) }
                }
            }
        }
    }
}

/* ---------------------------------- footer ---------------------------------- */

@Composable
private fun Footer() {
    val t = LT
    Div({ classes("qx-footer") }) {
        Div({ classes("qx-wrap") }) {
            Div({ classes("qx-footer-in") }) {
                Div({ classes("qx-footer-brand") }) {
                    Logo()
                    Div({ classes("qx-footer-tag") }) {
                        Text(t.footerTag)
                    }
                }
                Div({ classes("qx-footer-links") }) {
                    A(href = "#how", { attr("data-qk", "footer-how") }) { Text(t.navHow) }
                    A(href = "#pricing", { attr("data-qk", "footer-pricing") }) { Text(t.navPricing) }
                    A(href = "#faq", { attr("data-qk", "footer-faq") }) { Text(t.navFaq) }
                    A(href = "/admin", { attr("data-qk", "footer-login") }) { Text(t.navLogin) }
                }
                Div({ classes("qx-footer-contacts") }) {
                    Div({ classes("qx-footer-contacts-title") }) { Text(t.footerContacts) }
                    Span { Text("Gran Vía 6, Madrid, Spain") }
                    A(href = "mailto:support@qrkontaq.com") { Text("support@qrkontaq.com") }
                }
            }
            Div({ classes("qx-footer-bottom") }) {
                Span { Text("© 2026 QRkontaq") }
                Div({ classes("qx-footer-legal") }) {
                    A(href = "/terms") { Text(t.footerTerms) }
                    A(href = "/privacy") { Text(t.footerPrivacy) }
                    A(href = "/refund") { Text(t.footerRefund) }
                    A(
                        href = "https://www.trustpilot.com/review/qrkontaq.com",
                        {
                            attr("target", "_blank")
                            attr("rel", "noopener")
                        },
                    ) { Text("Trustpilot") }
                }
                Span { Text(t.footerMade) }
            }
            Div({ classes("qx-fword") }) { Text("QRkontaq") }
        }
    }
}
